/**
 * Whose eyes the app is drawn through.
 *
 * An owner or a partner may look at the business as one person on the team
 * sees it. While they do, two things are true everywhere at once: the band at
 * the top of every screen names that person, and nothing tapped writes. This
 * store holds the one fact both of those read.
 *
 * It is a zustand store and not a React context for the same reason
 * `sync-store` is: `useRecordGrant` and the tab shell read it, and neither sits
 * under a provider the perspective could be threaded through. It is never
 * persisted. A mode that survives a cold start is a mode somebody forgets they
 * are in, and every write they then try is refused by a fence they cannot see.
 *
 * The person is pinned to a business. Switching business ends the view, and
 * so does signing out; both callers say so with `exit()`, and `viewingAs`
 * answers null for any business but the one it was entered in, so a stale
 * value can never dress another business's screens.
 *
 * Source: docs/design/dashboard-view-as, the Plan and Fence artboards.
 */
import type { BusinessMember, GrantRow } from '@alluzo/shared';
import { create } from 'zustand';

export type Perspective = {
  /** The business the person was picked in. */
  readonly tenantId: string;
  readonly member: BusinessMember;
  /**
   * Every grant the member holds, keyed by record id, fetched when they were
   * picked. This is what makes the mode a real window: a record absent here is
   * a record they cannot open, so it is not drawn; `balance_visible` false is a
   * headline they never see, so it is not drawn either. Fetched once at enter
   * — the sheet refuses to enter without it, because a perspective whose
   * restrictions are unknown would silently show the owner's own view and call
   * it somebody else's.
   */
  readonly grants: Readonly<Record<string, GrantRow>>;
  /**
   * Whether the member's level reaches `view_business_insights`, answered from
   * the capability catalogue at enter. Decides whether the Dashboard tab is
   * drawn while the mode is on.
   */
  readonly holdsInsights: boolean;
  /**
   * Where their role is live (web-redesign-03 phase 3.4): the records whose
   * See entries reaches Their team, and who that team is (the person and
   * everyone below them, read when they were picked). Absent for a person
   * still on the old rows, whose `row_scope` says it all.
   */
  readonly teamRecords?: readonly string[];
  readonly team?: readonly string[];
};

type PerspectiveState = {
  perspective: Perspective | null;
  enter: (perspective: Perspective) => void;
  exit: () => void;
};

export const usePerspectiveStore = create<PerspectiveState>((set) => ({
  perspective: null,
  enter: (perspective) => set({ perspective }),
  exit: () => set({ perspective: null }),
}));

/**
 * The person the active business is being viewed as, or null when the owner
 * is themselves. Pure, so the fence can call it outside React.
 */
export const viewingAs = (
  perspective: Perspective | null,
  tenantId: string | null,
): BusinessMember | null =>
  perspective === null || tenantId === null || perspective.tenantId !== tenantId
    ? null
    : perspective.member;

/** The same answer, as a hook, re-rendering when the perspective changes. */
export const useViewingAs = (tenantId: string | null): BusinessMember | null =>
  usePerspectiveStore((state) => viewingAs(state.perspective, tenantId));

/**
 * True while ANY perspective is set, whatever the business.
 *
 * This is what the write fence reads. The fence must not need a tenant id to
 * answer, because `useRecordGrant` is keyed by record and knows no tenant, and
 * a perspective that is stale for the current business is exited on the switch
 * anyway. Erring towards "read only" is the safe side of this particular fence.
 */
export const useIsViewingAsSomeone = (): boolean =>
  usePerspectiveStore((state) => state.perspective !== null);

/**
 * The active perspective whatever the business, or null as myself.
 *
 * The grant map and the tab shell read this. Like the fence above, it takes no
 * tenant id: the grants are keyed by record id, another business's records are
 * simply absent from the map, and a stale perspective is exited on the switch.
 */
export const usePerspective = (): Perspective | null =>
  usePerspectiveStore((state) => state.perspective);

/**
 * Whose rows reach the person being viewed in one record, when their role
 * reaches Their team there; null when the grant's row scope says it all.
 * Pure, for the lens and its callers outside React.
 */
export const teamLensOf = (perspective: Perspective, recordId: string): ReadonlySet<string> | null =>
  perspective.teamRecords?.includes(recordId) === true ? new Set([perspective.member.userId, ...(perspective.team ?? [])]) : null;
